import { Autocomplete, Box, FormControl, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, TextField, Typography } from '@mui/material'
import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment, { Moment } from 'moment'
import TransactionService from '../../../shared/services/transaction/transaction.service'
import EmissionCalculationService, { ISupersedeDecision, SupersedeResolver } from '../../../shared/services/emission-calculation/emission-calculation.service'
import { ReportPeriodContext } from '../../../context/report-periods/report-periods'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { UserDetailsContext } from '../../../context/user-details/user-details'
import CustomFieldService from '../../../shared/services/custom-field/custom-field.service'
import { ICustomField } from '../../../shared/services/custom-field/custom-field.model'
import { EmissionsInputAdvanced } from '../../presentation-components/emissons-components/overview/emissions-input/emissions-input-advanced-form'
import { formatTransaction } from '../../../shared/utils/format-post-utils'
import SubsidiaryService from '../../../shared/services/subsidiary/subsidiary.service'
import { ISubsidiary } from '../../../shared/services/subsidiary/subsidiary.model'
import { getPreferredSubsidiary, getSelectableSubsidiaries } from '../../../shared/utils/subsidiary-utils'
import SpendBasedConversionService from '../../../shared/services/spend-based-conversion/spend-based-conversion.service'
import GroupService from '../../../shared/services/group/group.service'
import ConversionSourceService from '../../../shared/services/conversion-source/conversion-source.service'
import { ISpendBasedConversion } from '../../../shared/services/spend-based-conversion/spend-based-conversion.model'
import { IConversionSource } from '../../../shared/services/conversion-source/conversion-source.model'
import { IGroup } from '../../../shared/services/group/group.model'
import { sortGroupsByGhgCategory, formatGroupLabel } from '../../../shared/constants/ghg-category'
import ContactSupplierService from '../../../shared/services/contact-supplier/contact-supplier.service'
import { IContactSupplierDetails } from '../../../shared/services/contact-supplier/contact-supplier.model'
import { SupplierPicker } from '../../presentation-components/form-field-components/supplier-picker'
import { IAllowedInputDates } from '../../../context/report-periods/report-periods.model'
import { useIsReportPeriodLocked } from '../../../hooks/use-is-report-period-locked'
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels'
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper'
import { IEmission, IEmissionDetail } from '../../../shared/services/emission/emission.model'
import { IEmissionCalculation } from '../../../shared/services/emission-calculation/emission-calculation.model'
import { isEtsGroupId } from '../../../shared/utils/group-ids-utils'
import { resolveEmissionFieldFlags } from '../../../shared/utils/emission-field-config'
import { formatConversionCodeLabel, sortByConversionCode } from '../../../shared/utils/conversion-utils'
import { notifyError } from '../../../shared/utils/notify-error'
import { useDirtyBaseline } from '../../../hooks/use-dirty-baseline'

export const AddManualSpendBasedDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  successHandler: (emissionCalculationIds?: string[]) => void,
  emission?: IEmission,
  editCalculation?: IEmissionCalculation | null,
  editCalculations?: IEmissionCalculation[],
  editCalculationIds?: string[],
  resolveSupersede?: SupersedeResolver,
}> = ({
  open,
  closeHandler,
  successHandler,
  emission,
  editCalculation,
  editCalculations,
  editCalculationIds,
  resolveSupersede,
}) => {
    const isAddingToExisting = Boolean(emission)
    const isEditingCalculation = Boolean(editCalculationIds?.length)
    const spendEditCalculation = editCalculation ?? editCalculations?.find(calculation => calculation.type === 'SPEND') ?? null
    // The entered (original) spend, summed across calendar-year segments. A SPEND
    // row's unitsConsumed is the FX and inflation converted figure, so seeding the
    // form with it would round-trip a converted amount as if freshly entered and
    // convert it again on save. MEASUREMENT rows hold the entered spend per
    // segment; originalUnitsConsumed is the stored fallback when no MEASUREMENT
    // row exists (legacy-migrated entries); the converted figure is the last
    // resort, which the server's original-spend guard corrects.
    const originalSpendSeed = useMemo(() => {
      const calculations = editCalculations ?? (editCalculation ? [editCalculation] : [])
      const measurementRows = calculations.filter(calculation => calculation.type === 'MEASUREMENT' && calculation.unitsConsumed != null)
      if (measurementRows.length) {
        return measurementRows.reduce((sum, calculation) => sum + Number(calculation.unitsConsumed), 0)
      }
      const baseSpendRows = calculations.filter(calculation => calculation.type === 'SPEND' && !calculation.isMarketBased && !calculation.isWtt)
      if (baseSpendRows.length && baseSpendRows.every(calculation => calculation.originalUnitsConsumed != null)) {
        return baseSpendRows.reduce((sum, calculation) => sum + Number(calculation.originalUnitsConsumed), 0)
      }
      return spendEditCalculation?.unitsConsumed ?? null
    }, [editCalculations, editCalculation, spendEditCalculation])
    //Service
    const transactionService = new TransactionService()
    const emissionCalculationService = new EmissionCalculationService()
    const subsidiaryService = SubsidiaryService.getInstance()
    const customFieldService = new CustomFieldService()
    const contactSupplierService = ContactSupplierService.getInstance()
    const spendBasedConversionService = new SpendBasedConversionService()
    const conversionSourceService = new ConversionSourceService();
    const groupService = new GroupService();
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const reportPeriodContext = useContext(ReportPeriodContext);
    const userNotificationsContext = useContext(UserNotificationsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    const userDetails = userDetailsContext.userDetails
    //State
    const [contactSuppliers, setContactSuppliers] = useState<IContactSupplierDetails[]>()
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
    const [customFields, setCustomFields] = useState<ICustomField[]>()
    const fieldFlags = useMemo(
      () => resolveEmissionFieldFlags(userDetails?.organisation?.emissionFieldConfig, customFields ?? []),
      [userDetails?.organisation?.emissionFieldConfig, customFields],
    )
    const [spendBasedConversions, setSpendBasedConversions] = useState<ISpendBasedConversion[]>()
    const sortedSpendBasedConversions = useMemo(
      () => sortByConversionCode(spendBasedConversions),
      [spendBasedConversions],
    )
    const [groups, setGroups] = useState<IGroup[]>()
    const [conversionSources, setConversionSources] = useState<IConversionSource[]>()
    const [fileData, setFileData] = useState([]);
    const [openDate, setOpenDate] = useState(false);
    const [allowedDates, setAllowedDates] = useState<IAllowedInputDates>()
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    //Loading State
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    //Use Effects
    useEffect(() => {
      customFieldService.getAllActive(`&entryType=transaction`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => console.log(err))
      contactSupplierService.getActiveSuppliersLite(false)
        .then(response => {
          setContactSuppliers(response)
        })
        .catch(err => console.log(err))

      groupService.getGroups()
        .then(response => {
          setGroups(response)
        })
        .catch(err => console.log(err))

      conversionSourceService.getAll(true, 'spendBasedConversion')
        .then(response => {
          const existingConversionSourceId = spendEditCalculation?.spendBasedConversion?.conversionSourceId
          if (existingConversionSourceId && spendEditCalculation.spendBasedConversion?.conversionSource) {
            const hasCurrentSource = response.some(
              (source) => source.id === existingConversionSourceId,
            )
            if (!hasCurrentSource) {
              setConversionSources([
                ...response,
                {
                  id: existingConversionSourceId,
                  source: spendEditCalculation.spendBasedConversion.conversionSource.source,
                } as IConversionSource,
              ])
              return
            }
          }
          setConversionSources(response)
        })
        .catch(err => console.log(err))

      const reportPeriod = reportPeriodContext.allReportPeriods.find(reportPeriod => reportPeriod.id === reportPeriodContext.lastSelectedReportId)
      if (reportPeriod) {
        setAllowedDates({
          startDate: reportPeriod.startDate,
          endDate: reportPeriod.endDate,
        })
      }
    }, [emission])

    useEffect(() => {
      if (!conversionSources) return
      subsidiaryService.getAll()
        .then(response => {
          setSubsidiaries(response)
          if (spendEditCalculation) {
            const defaultId = spendEditCalculation.spendBasedConversion?.conversionSourceId
            if (defaultId && conversionSources.find(cs => cs.id === defaultId)) {
              formik.setFieldValue('conversionSourceId', defaultId)
              // This seed lands after a network round-trip, when the user may already be
              // typing: baseline only the field it wrote, never their edits.
              rebaseline(['conversionSourceId'])
              const year = moment(emission?.startDate).format('YYYY')
              spendBasedConversionService.getSpendBasedConversionsForConversionSource(defaultId, year, true, true)
                .then(conversions => {
                  const existingConversion = spendEditCalculation.spendBasedConversion && spendEditCalculation.conversionId
                    ? {
                        ...spendEditCalculation.spendBasedConversion,
                        id: spendEditCalculation.conversionId,
                      }
                    : null
                  const hasExistingConversion = existingConversion
                    ? conversions.some((conversion) => conversion.id === existingConversion.id)
                    : true
                  setSpendBasedConversions(
                    hasExistingConversion || !existingConversion
                      ? conversions
                      : [...conversions, existingConversion],
                  )
                  formik.setFieldValue('returnId', spendEditCalculation.conversionId ?? '')
                  rebaseline(['returnId'])
                })
                .catch(err => console.log(err))
            }
          } else if (emission) {
            const emissionSubsidiary = response.find(s => s.id === emission.subsidiaryId)
            const defaultId = emissionSubsidiary?.defaultSpendBasedConversionSourceId
            if (defaultId && conversionSources.find(cs => cs.id === defaultId)) {
              formik.setFieldValue('conversionSourceId', defaultId)
              rebaseline(['conversionSourceId'])
              const year = moment(emission.startDate).format('YYYY')
              spendBasedConversionService.getSpendBasedConversionsForConversionSource(defaultId, year, true, true)
                .then(conversions => setSpendBasedConversions(conversions))
                .catch(err => console.log(err))
            }
          } else {
            const preferred = getPreferredSubsidiary(response, userDetails)
            if (preferred) {
              formik.setFieldValue('subsidiaryId', preferred.id)
              formik.setFieldValue('countryId', preferred.countryId)
              // Baseline exactly the fields this leg wrote: conversionSourceId only when
              // it actually was written, so a source the user picked during the
              // round-trip stays dirty.
              const seededFields = ['subsidiaryId', 'countryId']
              const defaultConversionSourceId = preferred.defaultSpendBasedConversionSourceId
              if (conversionSources.find(cs => cs.id === defaultConversionSourceId)) {
                formik.setFieldValue('conversionSourceId', defaultConversionSourceId)
                // Load the activity list for the default source at open, so the dropdown is
                // never empty (or stale from a previous open) until another field is touched.
                spendBasedConversionService.getSpendBasedConversionsForConversionSource(defaultConversionSourceId, moment(formik.values.startDate || undefined).format('YYYY'), true, true)
                  .then(conversions => setSpendBasedConversions(conversions))
                  .catch(err => console.log(err))
                seededFields.push('conversionSourceId')
              }
              rebaseline(seededFields)
            }
          }
        })
        .catch(err => console.log(err))
    }, [conversionSources, emission, spendEditCalculation])

    useEffect(() => {
      if (open && emission) {
        formik.setFieldValue('startDate', emission.startDate)
        if (emission.contactSupplierId) {
          formik.setFieldValue('contactSupplierId', emission.contactSupplierId)
        }
        if (spendEditCalculation) {
          formik.setFieldValue('groupId', spendEditCalculation.groupId)
          formik.setFieldValue('unitsConsumed', originalSpendSeed ?? spendEditCalculation.unitsConsumed)
          formik.setFieldValue('returnId', spendEditCalculation.conversionId ?? '')
          formik.setFieldValue('conversionSourceId', spendEditCalculation.spendBasedConversion?.conversionSourceId ?? '')
          formik.setFieldValue('isFlag', spendEditCalculation.isFlag === true)
          formik.setFieldValue('flagCategory', spendEditCalculation.flagCategory ?? '')
        }
        // Full snapshot is safe here: this effect runs synchronously in the open
        // transition, before the user can have touched the form.
        rebaseline()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, emission, spendEditCalculation, originalSpendSeed])

    // Config-driven contextual fields (only collected when creating a new emission).
    // Supplier is intrinsic to a spend-based entry, so it stays required regardless of config.
    const reqContextual = (key: string, label: string) =>
      fieldFlags.mandatoryFields.includes(key)
        ? Yup.string().required(`${label} is required`)
        : Yup.string().nullable()
    const customFieldShape: Record<string, any> = {}
    ;(customFields ?? []).forEach(cf => {
      customFieldShape[cf.id] = fieldFlags.mandatoryFields.includes(cf.id)
        ? Yup.string().required(`${cf.fieldName} is required`)
        : Yup.string().nullable()
    })

    const fullEmissionSchema = Yup.object().shape({
      contactSupplierId: Yup.string().required('You must provide a supplier'),
      returnId: Yup.string().required('You must select an activity'),
      unitsConsumed: Yup.number().min(0.01, 'The amount must be greater than 0').required('You must provide an amount'),
      startDate: Yup.string().required('You must select a date'),
      groupId: Yup.string().required('You must select a group'),
      conversionSourceId: Yup.string().required('Emission Factor Source is required'),
      subsidiaryId: Yup.string().required(`${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} is required`),
      countryId: Yup.string().required('Country is required'),
      projectId: reqContextual('project', 'Project'),
      locationId: reqContextual('location', 'Location'),
      vehicleId: reqContextual('vehicle', 'Vehicle'),
      capitalGoodId: reqContextual('product', 'Product / Capital Good'),
      partOfEts: Yup.boolean(),
      regulatedEmissionTradingSchemeId: Yup.string().when('partOfEts', {
        is: true,
        then: schema => schema.required('You need to select a regulated emission trading scheme'),
        otherwise: schema => schema.nullable().notRequired()
      }),
      excludeFromReporting: Yup.boolean(),
      excludeFromReportingReason: Yup.string().when('excludeFromReporting', {
        is: true,
        then: (schema) => schema.required('You must provide a reason for exclusion'),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
      ...customFieldShape,
    })

    const addCalculationSchema = Yup.object().shape({
      contactSupplierId: Yup.string().required('You must provide a supplier'),
      returnId: Yup.string().required('You must select an activity'),
      unitsConsumed: Yup.number().min(0.01, 'The amount must be greater than 0').required('You must provide an amount'),
      groupId: Yup.string().required('You must select a group'),
      conversionSourceId: Yup.string().required('Emission Factor Source is required'),
    })

    const formik = useFormik<any>({
      initialValues: {
        subsidiaryId: '',
        countryId: '',
        groupId: '',
        unitsConsumed: undefined,
        returnId: '',
        startDate: '',
        contactSupplierId: '',
        conversionSourceId: '',
        projectId: '',
        locationId: '',
        vehicleId: '',
        capitalGoodId: '',
        partOfEts: false,
        regulatedEmissionTradingSchemeId: '',
        excludeFromReporting: false,
        excludeFromReportingReason: '',
        isFlag: spendEditCalculation?.isFlag === true,
        flagCategory: spendEditCalculation?.flagCategory ?? '',
      },
      validationSchema: emission ? addCalculationSchema : fullEmissionSchema,
      onSubmit: async (values) => {
        if (isReportPeriodLocked) {
          userNotificationsContext.openDialog({
            active: true,
            title: 'Action Not Allowed',
            message: `The selected report period is locked and cannot be modified`,
            buttonCopy: 'Ok',
            hideCancelButton: true
          })
          return
        }
        if (isSubmitting) return
        setIsSubmitting(true)
        if (emission) {
          const createPayload = {
            contactSupplierId: values.contactSupplierId || emission.contactSupplierId,
            groupId: values.groupId,
            returnId: values.returnId,
            conversionSourceId: values.conversionSourceId,
            unitsConsumed: values.unitsConsumed,
            type: 'SPEND',
            isFlag: values.isFlag === true,
            flagCategory: values.flagCategory || undefined,
          }
          let savedMessage = 'Saved successfully'
          let supersede = false
          if (!editCalculationIds?.length && resolveSupersede) {
            let decision: ISupersedeDecision
            try {
              decision = await resolveSupersede()
            } catch {
              setIsSubmitting(false)
              return
            }
            supersede = decision.supersede
            savedMessage = decision.savedMessage
          }
          const calculationAction = editCalculationIds?.length
            ? emissionCalculationService.editEmissionCalculation(editCalculationIds, createPayload)
            : emissionCalculationService.createEmissionCalculation(emission.id, createPayload, supersede)
          calculationAction
            .then((response) => {
              successHandler('emissionCalculationIds' in response ? response.emissionCalculationIds : undefined)
              setIsSubmitting(false)
              userNotificationsContext.openSnackbar({ open: true, message: savedMessage })
              // Baseline exactly the values that were submitted, so keystrokes typed
              // while the save was in flight stay dirty.
              rebaseline(() => values)
            })
            .catch(err => {
              if (err.response?.data?.code === 1) {
                userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' })
                console.error(err)
              } else {
                notifyError(userNotificationsContext.openSnackbar, err, 'saving the calculation')
              }
              setIsSubmitting(false)
            })
        } else {
          const payload = {
            ...values,
            relatedConversionModelName: 'spendBasedConversion',
            reportPeriodId: reportPeriodContext.lastSelectedReportId,
            endDate: values.startDate,
            dataType: 'Spend Data',
          }
          const response = formatTransaction(payload, payload.groupId, true, customFields)
          const form: any = new FormData()
          for (let i = 0; i < fileData.length; i++) {
            form.append('files', fileData[i])
          }
          form.append('transaction', JSON.stringify(response))
          transactionService.postTransaction(form)
            .then(() => {
              successHandler()
              setIsSubmitting(false)
              userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' })
              // Baseline exactly the values that were submitted, so keystrokes typed
              // while the save was in flight stay dirty.
              rebaseline(() => values)
            })
            .catch(err => {
              if (err.response?.data?.code === 1) {
                userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' })
                console.error(err)
              } else {
                notifyError(userNotificationsContext.openSnackbar, err, 'saving the emission')
              }
              setIsSubmitting(false)
            })
        }
      }
    });

    // The year the activity list is fetched for. A blank start date must never become
    // "Invalid date" in the query (the API cannot resolve it); fall back to today's year,
    // which the closest-active-year rule then resolves like any other unpublished year.
    const activityListYear = () => moment(formik.values.startDate || undefined).format('YYYY')
    // Dirty tracking. formik.dirty is unusable here: subsidiary, conversion source and
    // calculation values are seeded programmatically after load, which would mark the
    // form dirty on open. Each seeding path above calls rebaseline().
    const { isDirty, rebaseline } = useDirtyBaseline(formik.values)

    const conversionSourceHandler = (e: SelectChangeEvent<string>) => {
      formik.setFieldValue('conversionSourceId', e.target.value)
      if (e.target.value !== formik.values.conversionSourceId) {
        formik.setFieldValue('returnId', '')
        spendBasedConversionService.getSpendBasedConversionsForConversionSource(e.target.value, activityListYear(), true, true)
          .then(response => {
            setSpendBasedConversions(response)
          })
          .catch(err => console.log(err))
      }
    }

    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        formik.setFieldValue(name, date.format('YYYY-MM-DD'));
        const year = moment(date).format('YYYY');
        const prevYear = moment(formik.values.startDate).format('YYYY');
        const yearChanged = prevYear !== year;
        if (yearChanged) {
          spendBasedConversionService.getSpendBasedConversionsForConversionSource(formik.values.conversionSourceId, year, true, true)
            .then(response => {
              setSpendBasedConversions(response)
              const stillValid = response?.some(conversion => conversion.id === formik.values.returnId)
              if (!stillValid) {
                formik.setFieldValue('returnId', '')
              }
              return contactSupplierService.getAllActiveSuppliersWithUpdatedSpendBasedConversions(year)
                .then(updatedSuppliers => {
                  setContactSuppliers(updatedSuppliers)
                  const selectedSupplier = updatedSuppliers?.find(s => s.id === formik.values.contactSupplierId)
                  if (selectedSupplier?.spendBasedConversionId) {
                    const existsInReturns = response?.some(r => r.id === selectedSupplier.spendBasedConversionId)
                    if (existsInReturns) {
                      formik.setFieldValue('returnId', selectedSupplier.spendBasedConversionId)
                    }
                  }
                })
            })
            .catch(err => console.log(err))
        }
      }
    }

    const subsidiaryHandler = (e: SelectChangeEvent<string>) => {
      const subsidiary = subsidiaries?.find(s => s.id === e.target.value)
      if (subsidiary) {
        formik.setFieldValue('subsidiaryId', subsidiary.id)
        formik.setFieldValue('countryId', subsidiary.countryId)
        const defaultConversionSourceId = subsidiary.defaultSpendBasedConversionSourceId
        if (conversionSources?.find(conversionSource => conversionSource.id === defaultConversionSourceId)) {
          if (formik.values.conversionSourceId !== defaultConversionSourceId) {
            // The activity list must follow the source, or the dropdown keeps offering the
            // previous source's activities against the newly selected one.
            formik.setFieldValue('returnId', '')
            spendBasedConversionService.getSpendBasedConversionsForConversionSource(defaultConversionSourceId, activityListYear(), true, true)
              .then(response => setSpendBasedConversions(response))
              .catch(err => console.log(err))
          }
          formik.setFieldValue('conversionSourceId', defaultConversionSourceId)
        }
      }
    }

    const groupHandler = (e: SelectChangeEvent<string>) => {
      formik.handleChange(e)
      const newGroupId = e.target.value
      if (!isEtsGroupId(newGroupId)) {
        formik.setFieldValue('regulatedEmissionTradingSchemeId', '')
        formik.setFieldValue('partOfEts', false)
      }
    }

    const cancelHandler = () => {
      closeHandler()
    }

    return (
      <DialogWrapper
        open={open}
        onClose={cancelHandler}
        title={isEditingCalculation ? 'Edit Spend-Based Calculation' : 'Add Spend-Based Emission'}
        cancelHandler={cancelHandler}
        submitHandler={formik.submitForm}
        isSubmitting={isSubmitting}
        isDirty={isDirty}>
        {allowedDates && !isAddingToExisting &&
          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label='Date'
                value={formik.values.startDate ? moment(formik.values.startDate) : null}
                onChange={(date) => dateHandler(date, 'startDate')}
                format='DD/MM/YYYY'
                maxDate={moment(allowedDates.endDate)}
                minDate={moment(allowedDates.startDate)}
                sx={{ color: 'text.primary' }}
                open={openDate}
                onOpen={() => setOpenDate(true)}
                onClose={() => setOpenDate(false)}
                slotProps={{
                  textField: {
                    onClick: () => setOpenDate(true),
                    error: formik.touched.startDate && Boolean(formik.errors.startDate)
                  },
                }}
              />
            </LocalizationProvider>
          </FormControl>}
        {subsidiaries && (userDetails?.organisation?.enableSubsidiaries || subsidiaries.length > 1) && !isAddingToExisting &&
          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='subsidiaryId'>{screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}</InputLabel>
            <Select
              id='subsidiaryId' label='Subsidiary' name='subsidiaryId'
              value={formik.values.subsidiaryId || ''}
              onChange={subsidiaryHandler}
              error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
              sx={{ color: 'text.pprimary', }}>
              {getSelectableSubsidiaries(subsidiaries).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) =>
                <MenuItem key={index} value={subsidiary.id}>{subsidiary.name}</MenuItem>
              )}
            </Select>
            {formik.touched.subsidiaryId && formik.errors.subsidiaryId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.subsidiaryId ? <div>{formik.errors.subsidiaryId as string}</div> : null}
              </FormHelperText>
            }
          </FormControl>}
        {(contactSuppliers || (isAddingToExisting && emission?.contactSupplierId)) &&
          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            {isAddingToExisting && emission?.contactSupplierId ? (
              <TextField
                label={screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' })}
                value={(emission as IEmissionDetail).contactSupplier?.name ?? contactSuppliers?.find(s => s.id === emission.contactSupplierId)?.name ?? ''}
                disabled
                sx={{ color: 'text.primary' }}
              />
            ) : contactSuppliers ? (
            <SupplierPicker
              id="contactName"
              suppliers={contactSuppliers}
              value={formik.values.contactSupplierId}
              error={formik.touched.contactSupplierId && Boolean(formik.errors.contactSupplierId)}
              helperText={formik.touched.contactSupplierId && formik.errors.contactSupplierId ? formik.errors.contactSupplierId as string : null}
              onChange={(_id, newValue) => {
                // A supplier with a default spend source pulls the dialog onto it, mirroring the
                // import resolution ladder (supplier default beats subsidiary default). The switch
                // is announced by the caption under this field rather than happening silently,
                // and the source select stays free to override it afterwards.
                const selectedSupplier = newValue || undefined;
                formik.setFieldValue('contactSupplierId', selectedSupplier?.id || '');
                if (selectedSupplier?.defaultSpendBasedConversionSourceId) {
                  const defaultId = selectedSupplier.defaultSpendBasedConversionSourceId;
                  const existsInSources = conversionSources?.some(cs => cs.id === defaultId);
                  if (existsInSources) {
                    if (formik.values.conversionSourceId !== defaultId) {
                      formik.setFieldValue('returnId', '');
                      formik.setFieldValue('conversionSourceId', defaultId);
                      spendBasedConversionService
                        .getSpendBasedConversionsForConversionSource(
                          defaultId,
                          activityListYear(),
                          true,
                          true
                        )
                        .then(response => {
                          setSpendBasedConversions(response)
                          if (selectedSupplier?.spendBasedConversionId) {
                            const existsInReturns = response?.some(r => r.id === selectedSupplier.spendBasedConversionId)
                            if (existsInReturns) {
                              formik.setFieldValue('returnId', selectedSupplier.spendBasedConversionId)
                            }
                          }
                        })
                        .catch(err => console.log(err))
                    } else {
                      // Source unchanged: do not refetch conversions; set activity if valid in current list
                      if (selectedSupplier?.spendBasedConversionId) {
                        const existsInReturns = spendBasedConversions?.some(r => r.id === selectedSupplier.spendBasedConversionId)
                        if (existsInReturns) {
                          formik.setFieldValue('returnId', selectedSupplier.spendBasedConversionId)
                        }
                      }
                    }
                  }
                }
                // If no default source change, still set activity if available and valid for current list
                if (!selectedSupplier?.defaultSpendBasedConversionSourceId && selectedSupplier?.spendBasedConversionId) {
                  const existsInReturns = spendBasedConversions?.some(r => r.id === selectedSupplier.spendBasedConversionId)
                  if (existsInReturns) {
                    formik.setFieldValue('returnId', selectedSupplier.spendBasedConversionId)
                  }
                }
              }}
            />
            ) : null}
            {(() => {
              // The supplier-source relationship, stated whenever a supplier with a default is
              // selected: either the source followed the supplier (the flip above), or the user
              // overrode it afterwards. Either way nothing changes silently.
              const selectedSupplier = contactSuppliers?.find(supplier => supplier.id === formik.values.contactSupplierId)
              const supplierDefaultSource = selectedSupplier?.defaultSpendBasedConversionSourceId
                ? conversionSources?.find(cs => cs.id === selectedSupplier.defaultSpendBasedConversionSourceId)
                : undefined
              if (!supplierDefaultSource || !formik.values.conversionSourceId) return null
              return (
                <Typography variant='caption' sx={{ color: 'text.secondary', mt: 0.5, ml: 1.75 }}>
                  {supplierDefaultSource.id === formik.values.conversionSourceId
                    ? `Conversion source set to ${supplierDefaultSource.source}, this supplier's default.`
                    : `This supplier's default source is ${supplierDefaultSource.source}; this entry uses the conversion source selected below.`}
                </Typography>
              )
            })()}
          </FormControl>}
        {groups &&
          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='groupId'>Group</InputLabel>
            <Select
              id='groupId' label='Group' name='groupId'
              value={formik.values.groupId}
              onChange={groupHandler}
              error={formik.touched.groupId && Boolean(formik.errors.groupId)}
              sx={{ color: 'text.primary', }}>
              {sortGroupsByGhgCategory(groups).map((group, index) =>
                <MenuItem key={index} value={group.id}>{formatGroupLabel(group)}</MenuItem>
              )}
            </Select>
            {formik.touched.groupId && formik.errors.groupId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.groupId ? <div>{formik.errors.groupId as string}</div> : null}
              </FormHelperText>
            }
          </FormControl>}
        {conversionSources &&
          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='conversionSourceId'>Conversion Factor Source</InputLabel>
            <Select
              id='conversionSourceId' label='Conversion Factor Source' name='conversionSourceId'
              value={formik.values.conversionSourceId}
              onChange={conversionSourceHandler}
              error={formik.touched.conversionSourceId && Boolean(formik.errors.conversionSourceId)}
              sx={{ color: 'text.primary', }}>
              {conversionSources.sort((a, b) => a.source.localeCompare(b.source)).map((source, index) =>
                <MenuItem key={index} value={source.id}>{source.source}</MenuItem>
              )}
            </Select>
            {formik.touched.conversionSourceId && formik.errors.conversionSourceId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.conversionSourceId ? <div>{formik.errors.conversionSourceId as string}</div> : null}
              </FormHelperText>
            }
          </FormControl>}
        <FormControl fullWidth variant='outlined' sx={{ flexBasis: '200px', mt: 2 }}>
          <Autocomplete
            id="returnId"
            options={sortedSpendBasedConversions}
            freeSolo={false}
            autoHighlight
            getOptionLabel={(option) => typeof option === 'string' ? option : formatConversionCodeLabel(option)}
            renderOption={(props, option) => {
              const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
              return (
                <Box
                  component="li"
                  sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                  key={key}
                  {...restProps}
                >
                  {formatConversionCodeLabel(option, ' -')}
                </Box>
              )
            }}
            value={spendBasedConversions?.find(conversion => conversion.id === formik.values.returnId) || null}
            onChange={(event, newValue) => {
              formik.setFieldValue('returnId', typeof newValue === 'string' ? '' : newValue?.id || '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ color: 'text.primary' }}
                label="Spend Activity"
                error={formik.touched.returnId && Boolean(formik.errors.returnId)}
                helperText={formik.touched.returnId && formik.errors.returnId ? formik.errors.returnId as string : null}
              />
            )}
          />
        </FormControl>
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='unitsConsumed'>Amount in {userDetails?.organisation?.currencyCode} (including tax)</InputLabel>
          <OutlinedInput inputProps={{ min: 0 }} id='unitsConsumed' label={`Amount in ${userDetails?.organisation?.currencyCode} (including tax)`} name='unitsConsumed' type='number'
            value={formik.values.unitsConsumed}
            onChange={formik.handleChange}
            error={formik.touched.unitsConsumed && Boolean(formik.errors.unitsConsumed)}
            sx={{ color: 'text.primary' }}></OutlinedInput>
          {formik.touched.unitsConsumed && formik.errors.unitsConsumed &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.unitsConsumed as string}</FormHelperText>
          }
        </FormControl>
        {customFields && (
          emission ? (
            <EmissionsInputAdvanced
              customFields={customFields}
              formik={formik}
              hideCountry={true}
              hideLocation={true}
              hideSupplier={true}
              hideVehicle={true}
              hideProject={true}
              hideRec={true}
              hideCustomFields={true}
              hideRegulatedEmissionTradingScheme={true}
              hideExcludedFromReporting={true}
              hideCustomBasedMarketMultiplier={true}
              hideEnergySupplierTariff={true}
            />
          ) : (
            <EmissionsInputAdvanced
              hideRegulatedEmissionTradingScheme={fieldFlags.hideEts || !isEtsGroupId(formik.values.groupId)}
              customFields={fieldFlags.visibleCustomFields}
              formik={formik}
              hideCountry={fieldFlags.hideCountry}
              hideLocation={fieldFlags.hideLocation}
              hideSupplier={true}
              hideVehicle={fieldFlags.hideVehicle}
              hideProduct={fieldFlags.hideProduct}
              hideProject={fieldFlags.hideProject}
              hideExcludedFromReporting={fieldFlags.hideExcludeFromReporting}
              hideRec={true}
              hideCustomBasedMarketMultiplier={true}
              hideEnergySupplierTariff={true}
              mandatoryFields={fieldFlags.mandatoryFields}
            />
          )
        )}
      </DialogWrapper>
    )
  }